import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthProvider';
import type { SupabaseClient } from '../../lib/supabase';
import type { LeadActivity } from './leadActivity';
import type { MessageCommand, MessageSubject } from './messageSelection';
import type { Database } from '../../types/database.types';

export interface ActivityCorrectionCommand {
  activityId: string;
  patch: {
    subject_kind: MessageSubject['kind'];
    subject_id: string;
    expected: Pick<
      LeadActivity,
      | 'body'
      | 'occurred_at'
      | 'occurred_is_day'
      | 'list_id'
      | 'copy_set_id'
      | 'message_slot'
      | 'message_selected_at'
      | 'message_version'
    >;
    body?: string;
    occurred_on?: string;
    message?: {
      copy_set_id: string | null;
      slot: number | null;
      target_revision: string | null;
      name: string | null;
      body: string | null;
    };
  };
  reason: string;
  requestId: string;
}

/** The displayed source row, not a refetched row, owns this correction's CAS.
 * Leave the original instant alone unless the person actually changes its day.
 * The optional message is committed with the details by one RPC, not two saves. */
export function activityCorrectionCommand(
  subject: MessageSubject,
  activity: LeadActivity,
  body: string,
  occurredOn: string | undefined,
  reason: string,
  requestId: string,
  message?: MessageCommand,
): ActivityCorrectionCommand {
  if (!subject.id || !activity.id || !requestId || !reason.trim())
    throw new Error('Give a reason for this correction.');
  if (activity.source !== 'manual') throw new Error('Only manual entries can be corrected.');
  if (occurredOn !== undefined && !/^\d{4}-\d{2}-\d{2}$/.test(occurredOn))
    throw new Error('Choose a valid date.');
  if (
    message &&
    (message.activityId !== activity.id ||
      message.subject.kind !== subject.kind ||
      message.subject.id !== subject.id ||
      message.expectedCopy !== activity.copy_set_id ||
      message.expectedSlot !== activity.message_slot ||
      message.expectedSelectedAt !== activity.message_selected_at ||
      message.expectedVersion !== activity.message_version)
  )
    throw new Error('This selection belongs to a different displayed entry. Reload it.');
  if (body === activity.body && occurredOn === undefined && message === undefined)
    throw new Error('Change the notes, date or message before saving a correction.');
  return Object.freeze({
    activityId: activity.id,
    patch: Object.freeze({
      subject_kind: subject.kind,
      subject_id: subject.id,
      expected: Object.freeze({
        body: activity.body,
        occurred_at: activity.occurred_at,
        occurred_is_day: activity.occurred_is_day,
        list_id: activity.list_id,
        copy_set_id: activity.copy_set_id,
        message_slot: activity.message_slot,
        message_selected_at: activity.message_selected_at,
        message_version: activity.message_version,
      }),
      ...(body === activity.body ? {} : { body }),
      ...(occurredOn === undefined ? {} : { occurred_on: occurredOn }),
      ...(message === undefined
        ? {}
        : {
            message: Object.freeze({
              copy_set_id: message.copySetId,
              slot: message.slot,
              target_revision: message.revision,
              name: message.name,
              body: message.body,
            }),
          }),
    }),
    reason: reason.trim(),
    requestId,
  });
}

export async function saveActivityCorrection(
  client: SupabaseClient,
  input: ActivityCorrectionCommand,
) {
  type Args = Database['public']['Functions']['correct_activity']['Args'];
  const args = {
    p_activity: input.activityId,
    p_action: 'correct',
    p_patch: input.patch,
    p_reason: input.reason,
    p_request: input.requestId,
  } satisfies Args;
  const { data, error } = await client.rpc('correct_activity', args);
  if (error) throw new Error(error.message);
  if (!data || typeof data !== 'object' || Array.isArray(data) || typeof data.event_id !== 'number')
    throw new Error('Correction could not be confirmed. Retry the same save.');
  return data;
}

export function useSaveActivityCorrection() {
  const { client } = useAuth();
  const cache = useQueryClient();
  return useMutation({
    mutationFn: (command: ActivityCorrectionCommand) => saveActivityCorrection(client, command),
    onSettled: () => {
      void cache.invalidateQueries({ queryKey: ['tracker'] });
      void cache.invalidateQueries({ queryKey: ['journey-history'] });
    },
  });
}
